import { Author } from "./author";
import { Host } from "./host";
import { Member } from "./member";
import { PaymentItem } from "./payment-item";
import {
  asBoolean,
  asDate,
  asNumber,
  asString,
  isObject,
  parseList,
} from "./model-utils";
import {
  enumValue,
  CollectionType,
  CollectionStatus,
  DeliveryMode,
  DeliveryStatus,
  DiscountSource,
  DiscountType,
  InvoiceType,
  OrderSource,
  PaymentStatus,
} from "./enums";

export class Collection {
  [key: string]: any;
  constructor(json: any = {}) {
    Object.assign(this, json);
    this.collectionId = asString(json.collectionId);
    this.localOrderId = asString(json.localOrderId);
    this.offlineSyncStatus = asString(json.offlineSyncStatus);
    this.offlineSyncError = asString(json.offlineSyncError);
    this.syncedCollectionId = asString(json.syncedCollectionId);
    this.offlineSynced = asBoolean(json.offlineSynced);
    this.offlineNo = asString(json.offlineNo);
    this.setTarget = asBoolean(json.setTarget);
    this.amountCollected = asNumber(json.amountCollected);
    this.mandatory = asBoolean(json.mandatory);
    this.privateMode = asBoolean(json.privateMode);
    this.setMinimumAmount = asBoolean(json.setMinimumAmount);
    this.setSpecificAmount = asBoolean(json.setSpecificAmount);
    this.postAsComment = asBoolean(json.postAsComment);
    this.collectionType = enumValue(CollectionType, json.collectionType);
    this.expire = asBoolean(json.expire);
    this.returnNotification = asBoolean(json.returnNotification);
    this.returnedComplete = asBoolean(json.returnedComplete);
    this.reviewRequired = asBoolean(json.reviewRequired);
    this.reviewReason = asString(json.reviewReason);
    this.name = asString(json.name);
    this.accessPointId = asString(json.accessPointId);
    this.accessPointName = asString(json.accessPointName);
    this.orgId = asString(json.orgId);
    this.memberId = asString(json.memberId);
    this.orgName = asString(json.orgName);
    this.groupId = asString(json.groupId);
    this.groupName = asString(json.groupName);
    this.description = asString(json.description);
    this.profileId = asString(json.profileId);
    this.currency = asString(json.currency);
    this.country = asString(json.country);
    this.status = enumValue(CollectionStatus, json.status);
    this.paymentStatus = enumValue(PaymentStatus, json.paymentStatus);
    this.deliveryStatus = enumValue(DeliveryStatus, json.deliveryStatus);
    this.deliveryMode = enumValue(DeliveryMode, json.deliveryMode);
    this.deliveryAmount = asNumber(json.deliveryAmount);
    this.vehicleType = asString(json.vehicleType);
    this.deliveryComment = asString(json.deliveryComment);
    this.deliveryDestination = asString(json.deliveryDestination);
    this.amount = asNumber(json.amount);
    this.amountToBePaid = asNumber(json.amountToBePaid);
    this.totalAmount = asNumber(json.totalAmount);
    this.subAmount = asNumber(json.subAmount);
    this.vatPercentage = asNumber(json.vatPercentage);
    this.vatAmount = asNumber(json.vatAmount);
    this.vatIncludedInProductPrice = asBoolean(json.vatIncludedInProductPrice);
    this.vatOnlyCalculatedOnProfit = asBoolean(json.vatOnlyCalculatedOnProfit);
    this.vatExcludeFromDelivery = asBoolean(json.vatExcludeFromDelivery);
    this.vatAmountRound = asBoolean(json.vatAmountRound);
    this.vatMarkUp = asBoolean(json.vatMarkUp);
    this.discountAmount = asNumber(json.discountAmount);
    this.discountPercentage = asNumber(json.discountPercentage);
    this.discountValue = asNumber(json.discountValue);
    this.discountType = enumValue(DiscountType, json.discountType);
    this.discountSource = enumValue(DiscountSource, json.discountSource);
    this.minimumPayableAmount = asNumber(json.minimumPayableAmount);
    this.amountPaid = asNumber(json.amountPaid);
    this.amountLeftToBePaid = asNumber(json.amountLeftToBePaid);
    this.publicOrder = asBoolean(json.publicOrder);
    this.invoiceUrl = asString(json.invoiceUrl);
    this.invoiceType = enumValue(InvoiceType, json.invoiceType);
    this.transactionFeeBearer = asString(json.transactionFeeBearer);
    this.guestName = asString(json.guestName);
    this.target = asNumber(json.target);
    this.editMode = asBoolean(json.editMode);
    this.transferOrder = asBoolean(json.transferOrder);
    this.multiplePayment = asBoolean(json.multiplePayment);
    this.publishPayment = asNumber(json.publishPayment);
    this.multiCurrency = asBoolean(json.multiCurrency);
    this.checkSum = asString(json.checkSum);
    this.instantPaymentMade = asBoolean(json.instantPaymentMade);
    this.enableProductItemTracking = asBoolean(json.enableProductItemTracking);
    this.enforceGatePass = asBoolean(json.enforceGatePass);
    this.scheduledPayment = asBoolean(json.scheduledPayment);
    this.allowDependantSharing = asBoolean(json.allowDependantSharing);
    this.allowNegativeStock = asBoolean(json.allowNegativeStock);
    this.inventoryScopedProducts = asBoolean(json.inventoryScopedProducts);
    this.logisticsScopedProducts = asBoolean(json.logisticsScopedProducts);
    this.managedInvoice = asBoolean(json.managedInvoice);
    this.managedOwnerInvoiceId = asString(json.managedOwnerInvoiceId);
    this.bulkInvoice = asBoolean(json.bulkInvoice);
    this.orderDate = asDate(json.orderDate);
    this.startDate = asDate(json.startDate);
    this.endDate = asDate(json.endDate);
    this.createdAt = asDate(json.createdAt);
    this.updatedAt = asDate(json.updatedAt);
    this.publishDate = asDate(json.publishDate);
    this.returnDate = asDate(json.returnDate);
    this.dueDate = asDate(json.dueDate);
    this.estimatedTimeOfDelivery = asDate(json.estimatedTimeOfDelivery);
    this.deliveryStatusDate = asDate(json.deliveryStatusDate);
    this.channel = enumValue(OrderSource, json.channel);
    this.address = asString(json.address);
    this.mapUrl = asString(json.mapUrl);
    this.latitude = asNumber(json.latitude);
    this.longitude = asNumber(json.longitude);
    this.paymentItem = parseList(json.paymentItem, PaymentItem.fromJson);
    this.author = Author.fromJson(json.author);
    this.member = Member.fromJson(json.member);
    this.host = Host.fromJson(json.host);
    this.transferTo = Author.fromJson(json.transferTo);
    this.publishBy = Author.fromJson(json.publishBy);
    this.publicInfo = isObject(json.publicInfo)
      ? { ...json.publicInfo }
      : json.publicInfo;
    this.deliveryVendor = isObject(json.deliveryVendor)
      ? { ...json.deliveryVendor }
      : json.deliveryVendor;
    this.invoiceBankAccount = isObject(json.invoiceBankAccount)
      ? { ...json.invoiceBankAccount }
      : json.invoiceBankAccount;
    this.invoiceHeaders = isObject(json.invoiceHeaders)
      ? { ...json.invoiceHeaders }
      : json.invoiceHeaders;
    this.dataPoints = isObject(json.dataPoints)
      ? { ...json.dataPoints }
      : json.dataPoints;
    this.supportingDocuments = Array.isArray(json.supportingDocuments)
      ? json.supportingDocuments
      : undefined;
    this.deliveryHistory = Array.isArray(json.deliveryHistory)
      ? json.deliveryHistory
      : [];
    this.history = Array.isArray(json.history) ? json.history : undefined;
    this.paymentHistory = Array.isArray(json.paymentHistory)
      ? json.paymentHistory
      : undefined;
    this.payments = Array.isArray(json.payments) ? json.payments : undefined;
    this.inflowHistory = Array.isArray(json.inflowHistory)
      ? json.inflowHistory
      : undefined;
    this.notes = Array.isArray(json.notes) ? json.notes : undefined;
    this.deliveryInfo = isObject(json.deliveryInfo)
      ? { ...json.deliveryInfo }
      : json.deliveryInfo;
  }

  get contactName() {
    const contact = this.publicOrder ? this.publicInfo : this.author;
    const fullName = [contact?.firstName, contact?.lastName]
      .filter(Boolean)
      .join(" ");

    return contact?.name?.trim() || fullName || "";
  }

  static fromJson(json: any) {
    return isObject(json) ? new Collection(json) : json;
  }
}
